import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

// Icons
import { PencilIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import SnackbarContext from "../../../context/psikotest/SnackbarContext";
import LoaderContext from "../../../context/psikotest/LoaderContext";

// Helper
import errorHandler from "../../../helper/psikotest/errorHandler";
import logRender from "../../../helper/psikotest/logRender";

// Controller
import Ist from "../../../controller/psikotest/psikotest_system/ist";
import IstFormula from "../../../controller/psikotest/psikotest_system/ist_formula";

// Component
import TableFilter from "../../../components/psikotest/psikotest_system/TableFilter";
import Table from "../../../components/psikotest/psikotest_system/Table";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  // parse param id
  const location = useLocation();
  // remove trailing slash in production when reload/refresh
  const arrPath = location.pathname.replace(/\/$/, "").split("/");
  const paramIstId = arrPath[arrPath.length - 1];
  // required state
  const [ist, setIst] = useState<TPSIst>();
  const [list, setList] = useState<Array<TPSIstFormula>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "ist_formula/ListDetail" });
  }, []);

  const getList = useCallback(async () => {
    try {
      setMessage("Fetch Ist Formula");
      const ic = new Ist()
      const resIst = await ic.get(paramIstId)
      setIst(resIst.data.row)
      const cif = new IstFormula();
      const res = await cif.list(paramIstId);
      setList(res.data.list);
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [setNotif, setMessage, paramIstId]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <h2 className="text-center my-3 font-semibold text-lg">{ist?.subtest_name} Formula</h2>
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <IstList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function IstList(props: { list: Array<TPSIstFormula>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (ifor: TPSIstFormula) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const cif = new IstFormula();
          await cif.delete(ifor.ist_id.toString(), ifor);
          props.handleDeleteList(ifor.id);
          setNotif({ type: "success", message: "ist formula deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Age Range",
        accessor: "age_min",
        show: isMobile,
        Cell: (props: { row: { original: TPSIstFormula } }) => {
          let label = props.row.original.age_min.toString()
          if (props.row.original.age_min !== props.row.original.age_max) {
            label = label + " - " + props.row.original.age_max.toString()
          }
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {label}
            </Link>
          );
        },
      },
      {
        id: "age_range",
        classHeader: "text-left px-2",
        Header: <div title="Age Range">Age Range</div>,
        classDivHeader: "flex items-center",
        accessor: "age_min",
        show: !isMobile,
        Cell: (props: { row: { original: TPSIstFormula } }) => {
          let label = props.row.original.age_min.toString()
          if (props.row.original.age_min !== props.row.original.age_max) {
            label = label + " - " + props.row.original.age_max.toString()
          }
          return (
            <Link to={`${props.row.original.id}`} className="flex items-center text-blue-500 hover:text-blue-800">
              {label}
            </Link>
          );
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5 w-auto" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TPSIstFormula } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
